import React, { useEffect, useState } from "react";
import { Box, MenuItem, Select, makeStyles, Grid, CircularProgress } from "@material-ui/core";
import { CreateCSSProperties } from "@material-ui/core/styles/withStyles";
import SwitchVariant from "./SwitchVariant";
import AssignBreakpoint from "./AssignBreakpoint";
import { Events } from "./Constants";
import { cloneDeep, omit, isEqual, remove } from "lodash";

import { getC3ExpCollectionById, getC3ExperienceById, createC3Experience, updateC3ExpCollection, c3CreateVariantFromParent } from "./C3Services/contentControl";
import MoveExperience from "./MoveExperience";
const LoggedInUserState = require("shared/ui/user/LoggedInUserState");

interface VariantComponentProps {
    experience: any; // {ExperienceModel}, this is experience model.
    handleCreateVariant: (experienceCollection: any) => void,
    handleChangeVariant: (variantExperienceID: string) => void,
    handleSave: () => void
}

interface MapLangWithExperienceId {
    [expId: string] : string;
}

const useStyles = makeStyles<CreateCSSProperties>(() => ({
    placeholder: {
        color: "#ffffff",
    },
    boxStyles: {
        display: "flex",
        justifyContent: "center",
        marginBottom: "4px",
    },
    menuItemStyles: {
        color: "#000",
        backgroundColor: "#ffffff",
        width: "150px",
        height: "24px",
        cursor: "pointer",
        fontSize: "14px",
        "&:first-child": {
            backgroundColor: "#ffffff",
            "&:hover": {
                backgroundColor: "#ebf0f1",
            }
        }
    },
    selectStyles: {
        color:"#fff", 
        backgroundColor:"#000", 
        fontSize: "14px", 
        width: "100px", 
        height: "25px", 
        border: "1px", 
        borderStyle: "solid", 
        borderColor:"#c6c6c6", 
        borderRadius:"5px",
        "& .MuiSelect-icon": {
            top: "4px",
            color: "#ffffff",
            right: "10px",
            position: "absolute",
        },
    loaderStyle: {
        color: "#ffffff",
        top: "4px",
    }
    }
  }));

export default function VariantComponent(props: VariantComponentProps) {
    const {experience, handleChangeVariant, handleCreateVariant, handleSave} = props;
    const classes = useStyles();
    const [loading, setLoading] = useState<boolean>(false);
    const [collectionDetails, setCollectionDetails] = useState<any>();
    const [currentLanguageExperiences, setCurrentLanguageExperiences] = useState<any>([]); //Contain experienceDetails { expID, thershold, lang }[]
    const [allExperienceJSON, setAllExperienceJSON] = useState<any>(); //contain ExperienceJson all experinces in the collection.
    const [currentLangUniqueExperiences, setCurrentLangUniqueExperiences] = useState<string[]>([]); //unique experienceId of current Language.
    const [render, toggleRender] = useState(false);
    const [isBaseExperience, setIsBaseExperience] = useState<boolean>(false);
    const [baseExpId, setBaseExpId] = useState<string>("");
    const [isMultiLanguageExperience, setIsMultiLanguageExperience] = useState<boolean>(false);
    const [createBreakpointAfterSave,setCreateBreakpointAfterSave] = useState<boolean>(false);
    const [isBreakpointAlreadyCreated, setIsBreakpointAlreadyCreated] = useState<boolean>(false);
    const [switchVariantAfterSave, setswitchVariantAfterSave] = useState<boolean>(false);
    const [experienceIdToSwitch, setExperienceIdToSwitch] = useState("");

    useEffect(() => {
        getCollection();
    }, [render]);

    useEffect(() => {
        const executeExperienceSaved = (event) => {
            if (createBreakpointAfterSave) {
                event.stopImmediatePropagation();
                setCreateBreakpointAfterSave(false);
                addVariant(isBreakpointAlreadyCreated);
            }
        }

        const executeSwitchExperienceSaved = (event) => {
            if(switchVariantAfterSave){
                event.stopImmediatePropagation();
                setswitchVariantAfterSave(false);
                setExperienceIdToSwitch("");
                handleChangeVariant(experienceIdToSwitch);
            }
        }

        if(createBreakpointAfterSave){
            window.addEventListener("experienceSaved",executeExperienceSaved);
            return () => {
                window.removeEventListener("experienceSaved", executeExperienceSaved);
            };
        }

        if(switchVariantAfterSave){
            window.addEventListener("experienceSaved",executeSwitchExperienceSaved);
            return () => {
                window.removeEventListener("experienceSaved", executeSwitchExperienceSaved);
            };
        }

    },[createBreakpointAfterSave, switchVariantAfterSave]);
    
    useEffect(() => {
        if(collectionDetails){
            const currentLanguage = experience.get("language");
            const currentLanguageExperience = collectionDetails.experiences.filter(exp => exp.language === currentLanguage);
            setCurrentLanguageExperiences(currentLanguageExperience);
        }
    }, [collectionDetails]);

    useEffect(() => {
        const experienceList = currentLanguageExperiences.map((exp) => exp.experienceId);
        const unique_experiences = Array.from(new Set(experienceList)) as string[];
        // As one experience can be assigned to multiple threshold
        // So we have show only unique experience for switching in Nav Bar.
        setCurrentLangUniqueExperiences(unique_experiences);
    }, [currentLanguageExperiences]);

    useEffect(() => {
        if (allExperienceJSON && isBaseExperience) {
          currentLangUniqueExperiences.forEach((experienceId: string) => {
            const experience = allExperienceJSON[experienceId];
            if (experience) {
              const { parentExperienceId, id } = experience;
              if (!parentExperienceId) setBaseExpId(id);
            }
          });
        }
    }, [allExperienceJSON]);

    /**
     * This method return child experienceId of experienceId passed.
     * @param expId , experienceID for which we have to find child ExperienceId.
     * @param language, Language in which have to find ChildExperience.
     * @returns{string} child experienceId of expId.
     */
    const getChildExpIdForLang = (expId: string, language: string) => {
        const childExperienceId = collectionDetails.experiences
            .filter((expDetail) => expDetail.language === language)
            .map(expDetail => expDetail.experienceId)
            .find((experienceId :string) => allExperienceJSON[experienceId].parentExperienceId === expId) as string;
        return childExperienceId;
    };

    const handleLastBreakpoint = (updatedCollectionDetails, currentLangauge) => {
        // if only one breakpoint is remaining delete all the breakpoints and assign threshold zero
        const experienceList: string[] = []; 
        updatedCollectionDetails.experiences.forEach(({experienceId, language}) => {
            if ( language == currentLangauge ) {
                experienceList.push(experienceId);
            }
        });

        const unique_experiences = Array.from(new Set(experienceList)) as string[];
        if (unique_experiences.length == 1) {
            remove(updatedCollectionDetails.experiences as any[], ({experienceId})=> unique_experiences[0] == experienceId);
            updatedCollectionDetails.experiences.unshift({
                experienceId: unique_experiences[0],
                threshold: 0,
                language: currentLangauge
            })
        }
        return updatedCollectionDetails;
    }

    const getAllChildExperiencesForDeletion = (expId: string, language: string) => {
        //first get list of all the experiences in which we have to search for parentExpID
        let listToSearch: string[] = []
        // get all expIds of non-base experience
        collectionDetails.experiences.forEach((item) => {
            if (item.language!== language ) {
                listToSearch.push(item.experienceId);
            } 
        });
        const uniqueList = Array.from(new Set(listToSearch));
        let childExpIds: string[] = []
        uniqueList.forEach((item: string) => {
            if (allExperienceJSON[item].parentExperienceId == expId) {
                childExpIds.push(item);
            }
        });
        return childExpIds;
    };

    const handleMultipleThreshold = (currentLangExperiences, variantId, updatedCollectionDetails, allVariants) => {
        let newExperienceDetails = currentLangExperiences.find((item) =>
                    item.experienceId !== variantId && item.threshold!= null
                ) 
            // for case where we don't have a non-null threshold experience id we'll just fetch the first one to replace
            if (!newExperienceDetails) {
                newExperienceDetails = currentLangExperiences.find((item) => {
                    if (item.experienceId !== variantId) {
                        let mainindex = updatedCollectionDetails.experiences.findIndex((variant) => isEqual(variant, item));
                        //remove the record as we'll assign this id to a threshold ahead
                        updatedCollectionDetails.experiences.splice(mainindex, 1); 
                        return item;
                    }
                }    
            )
        }
            const expIdsToReplaceIndex:any = []
            allVariants.forEach((variantDetail) => {
                expIdsToReplaceIndex.push(updatedCollectionDetails.experiences.findIndex((item) => isEqual(item, variantDetail)));
            });
            if (newExperienceDetails) {
                expIdsToReplaceIndex.forEach((index) => {
                    updatedCollectionDetails.experiences[index].experienceId = newExperienceDetails.experienceId;
                });
            }
        return updatedCollectionDetails;
    }

      //handling of breakpoints if user deletes a variant
    const getUpdatedCollectionDetails = (variantId:string, language: string, collectionDetails:any) => {
        const currentLangExperiences = collectionDetails.experiences.filter(exp => exp.language === language);
        const allVariants = currentLangExperiences.filter((item) => item.experienceId === variantId);
        let updatedCollectionDetails = structuredClone(collectionDetails);

         //if experience is assigned to 1 threshold
        if (allVariants.length == 1) {
            let expToDelete = allVariants[0];

            if (allVariants[0].threshold == null) {
                // find the index in experience collection for this one
                const mainIndex = collectionDetails.experiences.findIndex((item) => item.experienceId == expToDelete.experienceId);
                updatedCollectionDetails.experiences.splice(mainIndex, 1);
            } else { //handle if null is not assigned to it
                //find any experience id other than current one and assign it to that breakpoint also              
                let newExperienceDetails = currentLangExperiences.find((item) =>
                    item.experienceId !== expToDelete.experienceId && item.threshold!= null
                )
                // for case where we don't have a non-null threshold experience id we'll just fetch the first one to replace
                if (!newExperienceDetails) {
                    newExperienceDetails = currentLangExperiences.find((item) => {
                        if (item.experienceId !== variantId) {
                            let mainindex = updatedCollectionDetails.experiences.findIndex((variant) => isEqual(variant, item));
                            //remove the record as we'll assign this id to a threshold ahead
                            updatedCollectionDetails.experiences.splice(mainindex, 1); 
                            return item;
                        }
                    }    
                )}
                const mainIndex = updatedCollectionDetails.experiences.findIndex((item) => isEqual(item, expToDelete));
                if (newExperienceDetails) {
                    updatedCollectionDetails.experiences[mainIndex].experienceId = newExperienceDetails.experienceId;
                }
                
            }
         } else {  
            // if experience is assigned to multiple thresholds
            // find any experience id other than current one and assign it to that breakpoint also
            updatedCollectionDetails = handleMultipleThreshold(currentLangExperiences, variantId, updatedCollectionDetails, allVariants); 
        }
        updatedCollectionDetails = handleLastBreakpoint(updatedCollectionDetails, language);
        return updatedCollectionDetails;
    }

    const handleChildChange = (userEvent) => {
        if (userEvent.event === Events.SWITCH_VARIANT) {
            if (Object.keys(experience.changed).length === 1) { 
                handleChangeVariant(userEvent.variantExperienceId);
            } else { // in case something on the editor isn't saved we save again and then switch variant
                setExperienceIdToSwitch(userEvent.variantExperienceId);
                setswitchVariantAfterSave(true);
                handleSave();
            }
            
        } else if (userEvent.event === Events.ADD_VARIANT) {
            handleCreateVariant(collectionDetails);
        } else { //for delete handle here
            if (window.confirm("Are you sure you want to Delete this Variant ?")) {
                let updatedCollectionDetails = getUpdatedCollectionDetails(
                    userEvent.variantExperienceId,
                    allExperienceJSON[userEvent.variantExperienceId].language,
                    collectionDetails
                    );
                let childexps = getAllChildExperiencesForDeletion(userEvent.variantExperienceId, experience.get("language"));
    
                // handle cases for all childexps in different languages if any
                if (childexps.length) {
                    childexps.forEach((expId) => {
                        updatedCollectionDetails = getUpdatedCollectionDetails(
                            expId,
                            allExperienceJSON[expId].language,
                            updatedCollectionDetails
                            );
                    })
                }
                
                // if user chooses to delete an experience he's not currently on/ delete a experience not on canvas
                // update and render
                if (userEvent.variantExperienceId !== userEvent.currentExperienceId) {
                    updateC3ExpCollection(updatedCollectionDetails).then(() => {
                        toggleRender((prev) => !prev);
                    });
                } else { 
                    // if user chooses to delete an experience he's currently on we get the unique experience and navigate to #1 as in legacy
                    //navigate to #1 and thus rerender
                    const currentLangExperiences = updatedCollectionDetails.experiences.filter(exp => exp.language === experience.get("language"));
                    const experienceList = currentLangExperiences.map((exp) => exp.experienceId);
                    const unique_experiences = Array.from(new Set(experienceList)) as string[];
                    updateC3ExpCollection(updatedCollectionDetails).then(() => {
                        handleChangeVariant(unique_experiences[0]);
                    });   
                }
            }
        }
    };

    const addVariant = (isBreakpointAlreadyCreated) => {
        const currentLangauge = experience.get("language");
        const _collections = cloneDeep(collectionDetails);
        const { defaultLanguage, experiences } = _collections;
        const allExpLanguages = experiences.map(exp => exp.language);

        let uniqueLanguages = Array.from(new Set(allExpLanguages)) as string[];
        let existingThreshold = _collections.experiences[0].threshold; 
        let mappedLangEperienceId: MapLangWithExperienceId;
        let parentExperienceId: string;

        if(currentLangauge === defaultLanguage) { //In Case of Base Experiences => Adding Breakpoint Variant to Current and all other languages according to Breakpoints.
             mappedLangEperienceId = _collections.experiences.reduce((acc, exp) => {
                acc[exp.language] = exp.experienceId;
                return acc;
            }, {});

        } else { // In Case of Non - Base Experiences => Adding Breakpoint Variant to Current language according to Breakpoints.
             mappedLangEperienceId = _collections.experiences.reduce((acc, exp) => {
                if(exp.language === currentLangauge){
                    acc[exp.language] = exp.experienceId;
                }
                return acc;
            }, {});
            uniqueLanguages = [currentLangauge];
            existingThreshold = currentLanguageExperiences[0].threshold;
            parentExperienceId = currentLanguageExperiences[0].experienceId;
        }
        const newExpCollectionToAdd : any[] = [];

        if(!isBreakpointAlreadyCreated){
            const thresholdToAssign = LoggedInUserState.get("companySettings").get("breakpoints")
                .getLowerBounds()
                .map((breakpoint) => {
                    const threshold = breakpoint.get("threshold")
                    if (existingThreshold !== threshold){
                        return threshold;
                    }
                })
                .filter(threshold => threshold);
            
            
            // Create Device variant according to Threshold
            uniqueLanguages.forEach(language => {
                const experienceId = mappedLangEperienceId[language]
                thresholdToAssign.forEach((threshold) =>{
                    newExpCollectionToAdd.push({
                        experienceId,
                        threshold,
                        language
                    });
                });
            });
        }

        //Now Create one Experience for all the languages in {uniqueLanguages};
        getC3ExperienceById(experience.get("id"))
        .then(experienceJson => {
            setAllExperienceJSON((prev: any) => ({
                ...prev,
                [experienceJson.id]: experienceJson
            }));

            return experienceJson;
        }).then((experienceJson) =>
        Promise.all(
            uniqueLanguages.map(language => {
                // let experienceId = mappedLangEperienceId[language];
                let clone : any;
                if(language === currentLangauge){
                    clone = omit(experienceJson, ["id", "version"]);
                } else {
                    const nonBaseExpId = getChildExpIdForLang(experience.get("id"), language);
                    clone = omit(allExperienceJSON[nonBaseExpId], ["id", "version"]);
                }
                clone.name = `${clone.name} (new)`;
                clone.version = 1;

                return createC3Experience(clone);
            })
        )).then(experienceJsonArr => {
            let newBaseExpId: string;
            experienceJsonArr.forEach(({id, language}) => {
                if(language === defaultLanguage) { 
                    newBaseExpId = id;
                }
                newExpCollectionToAdd.push({
                    experienceId: id,
                    threshold: null,
                    language
                });
            });

            experienceJsonArr.forEach(({id, language}) => {
                // for base experience i.e exp with default language , parent will be exp with threshold 0.
                if(language === defaultLanguage) { 
                    parentExperienceId = experiences.find(({language, threshold}) => 
                        (language === defaultLanguage) && (threshold === 0)).experienceId;
                        c3CreateVariantFromParent(parentExperienceId, id)
                        .then(() => {
                            handleChangeVariant(newBaseExpId);
                        });
                } else {
                    c3CreateVariantFromParent(newBaseExpId, id);
                }
            })
        }).then(() => {
            _collections.experiences.push(...newExpCollectionToAdd);
            updateC3ExpCollection(_collections);
        });
    }

    // This method is used to create/add device variant.
    const createDeviceVariant = (isBreakpointAlreadyCreated = false) => {
        // if everything is already saved we create a variant without triggering save again
        if (Object.keys(experience.changed).length === 1) { 
            addVariant(isBreakpointAlreadyCreated);
        } else { // in case something on the editor isn't saved we save again and then create variant
            setCreateBreakpointAfterSave(true);
            setIsBreakpointAlreadyCreated(isBreakpointAlreadyCreated);
            handleSave();
        }

    }

    const getCollection = () => {
        try {
            const query = new URLSearchParams(window.location.search),
                expCollectionId = query.get("collectionId");

            setLoading(true)
            const a = getC3ExpCollectionById(expCollectionId)
                a.then((data) => {
                    //here data represents collection
                    setCollectionDetails(data);
                    const currentLanguage = experience.get("language");
                    const { experiences, defaultLanguage } = data;
                    if (currentLanguage === defaultLanguage) {
                       setIsBaseExperience(true); 
                    } else {
                        setIsBaseExperience(false);
                    };
                    setLoading(false);
                    const allUniqueExperiences = Array.from(new Set(experiences.map(({experienceId})=> experienceId))) as string[];
                    const allLanguages = Array.from(new Set(experiences.map(({language})=> language))) as string[];
                    allLanguages.length > 1 ? setIsMultiLanguageExperience(true): setIsMultiLanguageExperience(false);       
                    return allUniqueExperiences;
                })
                .then((allUniqueExperiences: any) => {
                    if(!allUniqueExperiences.length) return;
                    //Fetch all the experienceJson of current language
                    Promise.all(
                        allUniqueExperiences.map((experienceId : string) => getC3ExperienceById(experienceId)) // Fetch all Exp.
                    ).then((experienceJsonArr) => {
                        setAllExperienceJSON(
                            experienceJsonArr.reduce((obj, experienceJSON: any) => {
                                obj[experienceJSON.id] = experienceJSON;
                                return obj;
                            }, {})
                        );
                    });
                });
        } catch (error) {
            console.log(error);
        }
    };

    const renderLoader = () => (
        /* @ts-ignore */
        <Box
            style={{ display: "flex", justifyContent: "center", alignItems: "center" }}
        >
            <CircularProgress size={25} classes={
                {
                    colorPrimary:classes.loaderStyle
                }
            } />
        </Box>
    );

    const DropdownLabel = ({ children }) => {
        return <div className = {classes.placeholder}>{children}</div>;
      };

    const renderDropdown = () => {
        return(
            /* @ts-ignore */
            <Box className={classes.boxStyles} >
                <Select className = {classes.selectStyles}
                    disableUnderline = {true}
                    MenuProps = {{
                        anchorOrigin: {
                            vertical: "bottom",
                            horizontal: "left"
                        },
                        getContentAnchorEl: null
                    }}
                    id = "breakpoint-variant-dropdown"
                    value = "Variants"
                    onChange = {() => createDeviceVariant(false)}
                    renderValue = {() => <DropdownLabel>Variants</DropdownLabel>}
                >
                    <MenuItem className = {classes.menuItemStyles}>Breakpoint Variant</MenuItem>
                </Select>
            </Box>
        );
    };

    return (
        <>
        
            {
            loading ? renderLoader() :(
                currentLangUniqueExperiences.length > 1 ? (
                <Grid container direction="row" alignItems="center">
                    <SwitchVariant
                        uniqueExperiences = {currentLangUniqueExperiences}
                        handleChildEvent={handleChildChange}
                        currentExperienceId={experience.id}
                        createDeviceVariant = {createDeviceVariant}
                        baseExperience = {isBaseExperience}
                        baseExperienceId = {baseExpId}
                    />{ isBaseExperience &&
                        <AssignBreakpoint 
                        currentLanguageExperiences = {currentLanguageExperiences}
                        currentLanguageExpJSON = {allExperienceJSON}
                        uniqueExperiences = {currentLangUniqueExperiences}
                        collectionDetails = {collectionDetails}
                        setCollectionDetails = {setCollectionDetails}
                        getChildExpIdForLang = {getChildExpIdForLang}
                    />
                    } { !isMultiLanguageExperience && 
                        <MoveExperience
                        handleChangeVariant={handleChangeVariant}
                        collectionDetails = {collectionDetails}
                        baseExperienceId = {baseExpId}
                        setAllExperienceJSON = {setAllExperienceJSON}/> }
                    
                </Grid>
            ) : (
                 isBaseExperience && renderDropdown()
            ))}
        </>
    );
}



// WEBPACK FOOTER //
// ./src/editor/ui/editor/CollectionNavigation/VariantComponent.tsx